import { ArrowLeft, Bookmark, Bot, ChartColumn, Plug, Users, Cpu, GitBranch, Server, Settings2, Shield, Cloud } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import TopBar, { SearchButton } from '../../components/TopBar';
import { Spinner } from '../../components/ui';
import { useProject, useProjects } from '../../lib/queries';
import { OWNER_HINT, RoleContext, VIEWER_HINT } from '../../lib/role';
import AccountPage from './AccountPage';
import AgentsPage from './AgentsPage';
import ConnectionsPage from './ConnectionsPage';
import GeneralPage from './GeneralPage';
import IntegrationsPage from './IntegrationsPage';
import MemoryPage from './MemoryPage';
import ProvidersPage from './ProvidersPage';
import ServerPage from './ServerPage';
import SharingPage from './SharingPage';
import UsagePage from './UsagePage';
import HostingPage from './HostingPage';

const PROJECT_TABS = [
  { id: 'general', label: 'General', icon: Settings2 },
  { id: 'agents', label: 'Agents', icon: Bot },
  { id: 'server', label: 'Server connection', icon: Server },
  { id: 'hosting', label: 'Hosting', icon: Cloud },
  { id: 'integrations', label: 'GitHub & Jira', icon: GitBranch },
  { id: 'memory', label: 'Memory', icon: Bookmark },
  { id: 'sharing', label: 'Sharing', icon: Users },
] as const;
const ACCOUNT_TABS = [
  { id: 'connections', label: 'Connections', icon: Plug },
  { id: 'providers', label: 'Providers & models', icon: Cpu },
  { id: 'account', label: 'Account & security', icon: Shield },
  { id: 'usage', label: 'Usage', icon: ChartColumn },
] as const;

type ProjectTab = (typeof PROJECT_TABS)[number]['id'];

export default function Settings() {
  const { slug: urlSlug, tab = 'agents' } = useParams();
  const { data: projects, isLoading } = useProjects();
  // Account tabs have no project in the URL: use the last-used project for the nav and "Back".
  const lastUsed = [...(projects ?? [])].filter((p) => !p.archived).sort((a, b) => b.lastActiveAt.localeCompare(a.lastActiveAt))[0];
  const slug = urlSlug ?? lastUsed?.slug;
  const { project } = useProject(slug);
  const isProjectTab = PROJECT_TABS.some((t) => t.id === tab);
  const nav = useNavigate();
  const accountTabs = ACCOUNT_TABS;

  if (!PROJECT_TABS.some((t) => t.id === tab) && !ACCOUNT_TABS.some((t) => t.id === tab)) return <Navigate to={urlSlug ? `/p/${urlSlug}/settings/agents` : '/settings/providers'} replace />;
  if (isProjectTab && !urlSlug) return slug ? <Navigate to={`/p/${slug}/settings/${tab}`} replace /> : isLoading ? null : <Navigate to="/settings/providers" replace />;

  let content: ReactNode;
  if (isProjectTab) {
    if (!project?.id) content = <div style={{ padding: 40 }}>{isLoading ? <Spinner /> : 'Project not found.'}</div>;
    else {
      const pages: Record<ProjectTab, ReactNode> = {
        general: <GeneralPage project={project} />,
        agents: <AgentsPage project={project} />,
        server: <ServerPage project={project} />,
        hosting: <HostingPage project={project} />,
        integrations: <IntegrationsPage project={project} />,
        memory: <MemoryPage project={project} />,
        sharing: <SharingPage project={project} />,
      };
      // Settings you can only read: say so up front (the server refuses the change either way).
      const role = project.role ?? 'viewer';
      const notice = tab === 'sharing' ? null : role === 'viewer' ? VIEWER_HINT : tab === 'server' && role !== 'owner' ? `${OWNER_HINT} You can see the connection but not change it.` : null;
      content = (
        <RoleContext.Provider value={role}>
          {notice && (
            <div className="banner" role="note" style={{ margin: '0 0 16px' }}>
              {notice}
            </div>
          )}
          {pages[tab as ProjectTab]}
        </RoleContext.Provider>
      );
    }
  } else
    content =
      tab === 'connections' ? (
        <ConnectionsPage />
      ) : tab === 'providers' ? (
        <ProvidersPage />
      ) : tab === 'account' ? (
        <AccountPage />
      ) : (
        <UsagePage />
      );

  return (
    <div className="screen">
      <TopBar
        settings={false}
        left={
          <button className="btn btn-secondary" onClick={() => nav(slug ? `/p/${slug}` : '/')}>
            <ArrowLeft className="icon" />
            {slug ? 'Back to workspace' : 'Back to projects'}
          </button>
        }
        right={<SearchButton />}
      />
      <div className="settings">
        <nav className="settings-nav card" aria-label="Settings">
          {slug && (
            <>
              <div className="micro group">Project · {project?.name ?? '…'}</div>
              {PROJECT_TABS.map((t) => (
                <Link key={t.id} className="nav-item" to={`/p/${slug}/settings/${t.id}`} aria-current={tab === t.id ? 'page' : undefined}>
                  <t.icon className="icon" />
                  {t.label}
                </Link>
              ))}
            </>
          )}
          <div className="micro group account">Account</div>
          {accountTabs.map((t) => (
            <Link key={t.id} className="nav-item" to={`/settings/${t.id}`} aria-current={tab === t.id ? 'page' : undefined}>
              <t.icon className="icon" />
              {t.label}
            </Link>
          ))}
        </nav>
        <main className="settings-main">{content}</main>
      </div>
    </div>
  );
}
